import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import usePermissoes from '../hooks/usePermissoes';
import Permissoes from '../permissoes';

const DRAWER_WIDTH = 250;

// 'Documento' não é uma permissão real (a listagem é liberada a qualquer
// usuário autenticado), por isso não vem de Permissoes.
const ICONS = {
  [Permissoes.FEED]: DynamicFeedOutlinedIcon,
  [Permissoes.FUNCIONARIO]: PeopleOutlineIcon,
  [Permissoes.VEICULO]: DirectionsCarOutlinedIcon,
  [Permissoes.RELATORIO]: AssessmentOutlinedIcon,
  [Permissoes.MERCADORIA]: Inventory2OutlinedIcon,
  [Permissoes.ALMOXARIFADO]: WarehouseOutlinedIcon,
  [Permissoes.EPI]: HealthAndSafetyOutlinedIcon,
  [Permissoes.ROMANEIO]: LocalShippingOutlinedIcon,
  [Permissoes.FLUXO_CAIXA]: AccountBalanceWalletOutlinedIcon,
  [Permissoes.SETOR]: ApartmentOutlinedIcon,
  [Permissoes.SC]: ShoppingCartOutlinedIcon,
  [Permissoes.PROPOSTA_COMERCIAL]: DescriptionOutlinedIcon,
  [Permissoes.MEDICAO]: StraightenOutlinedIcon,
  [Permissoes.ORDEM_SERVICO]: AssignmentOutlinedIcon,
  Documento: FolderOutlinedIcon,
};

function SidebarMenu({ onCloseSidebar }) {
  const { permissoes } = usePermissoes();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const funcionalidades = [
    { id: Permissoes.FEED, label: 'Feed', path: '/feed', semprePermitido: true },
    { id: Permissoes.FUNCIONARIO, label: 'Funcionários', path: '/funcionario' },
    { id: Permissoes.VEICULO, label: 'Veículos', path: '/veiculo' },
    { id: Permissoes.RELATORIO, label: 'Relatórios', path: '/relatorio' },
    { id: Permissoes.MERCADORIA, label: 'Mercadorias', path: '/mercadoria' },
    { id: Permissoes.ALMOXARIFADO, label: 'Almoxarifado', path: '/almoxarifado' },
    { id: Permissoes.EPI, label: 'EPI', path: '/epi' },
    { id: Permissoes.ROMANEIO, label: 'Romaneio', path: '/romaneio' },
    { id: Permissoes.FLUXO_CAIXA, label: 'Fluxo de caixa', path: '/fluxoCaixa' },
    { id: Permissoes.SETOR, label: 'Setor', path: '/setor' },
    { id: Permissoes.SC, label: 'Solicitação de compra', path: '/sc' },
    { id: Permissoes.PROPOSTA_COMERCIAL, label: 'Proposta comercial', path: '/propostacomercial' },
    { id: Permissoes.MEDICAO, label: 'Medição', path: '/medicao' },
    { id: Permissoes.ORDEM_SERVICO, label: 'Ordem de serviço', path: '/os' },
    { id: 'Documento', label: 'Documentos', path: '/documento', semprePermitido: true },
  ];

  const funcionalidadesFiltradas = funcionalidades.filter(
    (func) => func.semprePermitido || permissoes.includes(func.id)
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? true : undefined}
      onClose={onCloseSidebar}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: isMobile ? 'fixed' : 'relative',
          height: '100%',
          backgroundColor: 'primary.dark',
          color: '#e8edf2',
          border: 'none',
        },
      }}
    >
      <List sx={{ py: 1 }}>
        {funcionalidadesFiltradas.map((func) => {
          const Icon = ICONS[func.id] || DescriptionOutlinedIcon;
          const isActive = location.pathname === func.path;
          return (
            <ListItemButton
              key={func.id}
              component={Link}
              to={func.path}
              onClick={onCloseSidebar}
              selected={isActive}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 2,
                color: 'inherit',
                '&.Mui-selected': {
                  backgroundColor: 'secondary.main',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={func.label}
                primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
}

export default SidebarMenu;
